import { useAppDispatch } from '@app/hooks/reduxHooks';
import { ImageModel, OrderModel } from '@app/interfaces/order';
import { removeImageOrder, uploadImageOrder } from '@app/store/slices/orderSlice';
import { Button, Image, message, Popconfirm, Tooltip, UploadFile } from 'antd';
import React, { useState } from 'react';
import UploadImage from './UploadImage';
import { DeleteOutlined, FileImageOutlined, PictureOutlined, UploadOutlined } from '@ant-design/icons';
import { readUser } from '@app/services/localStorage.service';

interface OrderInfoProps {
  record: OrderModel;
  assetType: string;
  description: string;
}

/** Chú thích phụ (số ảnh, cảnh báo xoá) dùng chung một kiểu chữ mờ. */
const HINT_CLASS = 'tw-text-xs tw-text-outline';

/** Ô ảnh vuông; `tw-group` để caption/nút xoá chỉ hiện khi hover vào ô. */
const THUMB_CLASS =
  'tw-group tw-relative tw-aspect-square tw-overflow-hidden tw-rounded-[10px] tw-border tw-border-solid tw-border-outline-variant tw-bg-surface-subtle tw-transition-[border-color,box-shadow] tw-duration-200 hover:tw-border-primary hover:tw-shadow-[0_2px_10px_rgba(0,0,0,0.08)]';

const ListImages: React.FC<OrderInfoProps> = ({ record, assetType, description }) => {
  const [open, setOpen] = useState(false);

  const dispatch = useAppDispatch();
  const orderId = Number(record?.id);
  const currentUser = readUser();

  const images: ImageModel[] = (record.images ?? []).filter((image) => image.asset_type === assetType);

  const onUpload = async (fileList: UploadFile[]) => {
    try {
      const formData = new FormData();

      fileList?.forEach((file: any, index: number) => {
        if (!file.originFileObj) return;

        formData.append(`order_assets_attributes[${index}][asset_type]`, assetType);
        formData.append(`order_assets_attributes[${index}][image]`, file.originFileObj);
        formData.append(`order_assets_attributes[${index}][name]`, file.name);
        formData.append(`order_assets_attributes[${index}][content_type]`, file.type);
        formData.append(`order_assets_attributes[${index}][size]`, file.size.toString());
        formData.append(`order_assets_attributes[${index}[created_by]`, currentUser?.name || '');
        formData.append(`order_assets_attributes[${index}[_destroy]`, 'false');
      });

      await dispatch(uploadImageOrder({ orderId, formData }));
      setOpen(false);
    } catch (error: any) {
      message.error(error?.message || 'Tải ảnh lên thất bại');
    }
  };

  const handleRemoveImage = async (image: ImageModel) => {
    try {
      await dispatch(removeImageOrder({ orderId, imageId: Number(image.id) }));
    } catch (error: any) {
      message.error(error?.message || 'Xoá ảnh thất bại');
    }
  };

  return (
    <>
      <div className="tw-mb-2.5 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
        <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
          <FileImageOutlined className="tw-shrink-0 tw-text-base tw-text-primary" />
          <span className="tw-text-[13px] tw-font-semibold tw-text-on-surface">{description}</span>
          <span className={HINT_CLASS}>
            {images.length > 0 ? `${images.length} ảnh` : 'chưa có ảnh'}
          </span>
        </span>

        <Button type="primary" size="small" icon={<UploadOutlined />} onClick={() => setOpen(true)}>
          Tải ảnh lên
        </Button>
      </div>

      {images.length > 0 ? (
        <Image.PreviewGroup>
          {/* auto-fill để số cột tự co theo bề rộng panel, ảnh luôn vuông. */}
          <div className="tw-grid tw-grid-cols-[repeat(auto-fill,minmax(110px,1fr))] tw-gap-3 sm:tw-grid-cols-[repeat(auto-fill,minmax(140px,1fr))]">
            {images.map((image) => (
              <div key={image.id} className={THUMB_CLASS}>
                {/* Ép cả wrapper .ant-image lẫn <img> lấp đầy ô vuông. */}
                <Image
                  src={image.url}
                  alt={image.name}
                  wrapperClassName="tw-block tw-h-full tw-w-full"
                  className="tw-block tw-h-full tw-w-full tw-object-cover"
                />

                {/* Dải tên/người tải nằm dưới đáy ảnh, chỉ hiện khi hover. */}
                <div className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-bottom-0 tw-z-[1] tw-bg-gradient-to-t tw-from-black/65 tw-to-black/0 tw-px-2 tw-pb-1.5 tw-pt-3.5 tw-text-[11px] tw-leading-[1.3] tw-text-white tw-opacity-0 tw-transition-opacity tw-duration-200 group-hover:tw-opacity-100">
                  <span className="tw-block tw-truncate tw-font-medium">{image.name}</span>
                  {image.created_by && <span className="tw-opacity-[0.85]">bởi {image.created_by}</span>}
                </div>

                <Popconfirm
                  title={
                    <>
                      Xoá ảnh này?
                      <div className={HINT_CLASS}>Ảnh sẽ bị xoá khỏi đơn hàng, không khôi phục được.</div>
                    </>
                  }
                  okText="Xoá"
                  okButtonProps={{ danger: true }}
                  cancelText="Huỷ"
                  onConfirm={() => handleRemoveImage(image)}
                >
                  <Tooltip title="Xoá ảnh">
                    {/* Nút xoá chỉ hiện khi hover/focus để lưới ảnh không bị rối. */}
                    <Button
                      className="tw-absolute tw-right-1.5 tw-top-1.5 tw-z-[2] tw-opacity-0 tw-transition-opacity tw-duration-200 focus-within:tw-opacity-100 group-hover:tw-opacity-100"
                      danger
                      type="primary"
                      size="small"
                      shape="circle"
                      icon={<DeleteOutlined />}
                    />
                  </Tooltip>
                </Popconfirm>
              </div>
            ))}
          </div>
        </Image.PreviewGroup>
      ) : (
        // Khung nét đứt gợi ý đây là vùng có thể tải ảnh lên.
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-rounded-[10px] tw-border-[1.5px] tw-border-dashed tw-border-outline-variant tw-bg-surface-subtle tw-px-4 tw-py-7 tw-text-center">
          <PictureOutlined className="tw-text-[30px] tw-text-outline-variant" />
          <span className="tw-text-[13px] tw-text-outline">Chưa có hình ảnh nào được tải lên.</span>
          <Button size="small" icon={<UploadOutlined />} onClick={() => setOpen(true)}>
            Tải ảnh lên
          </Button>
        </div>
      )}

      {open && <UploadImage open={open} onUpload={onUpload} onCancel={() => setOpen(false)} />}
    </>
  );
};

export default ListImages;
